import { useSyncExternalStore } from "react";
import { ROLES, type Role } from "@family/shared";

/**
 * Admin-only UI preview: temporarily render the app as if the current user
 * had a lower role. Purely client-side — the backend still enforces real
 * permissions. Persisted in localStorage so it survives reloads.
 */

const KEY = "family.preview-role";

function readInitial(): Role | null {
  try {
    const v = localStorage.getItem(KEY);
    return (ROLES as readonly string[]).includes(v ?? "") ? (v as Role) : null;
  } catch {
    return null;
  }
}

let current: Role | null = readInitial();
const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function getPreviewRole(): Role | null {
  return current;
}

export function setPreviewRole(next: Role | null): void {
  current = next;
  try {
    if (next) localStorage.setItem(KEY, next);
    else localStorage.removeItem(KEY);
  } catch {
    // ignore quota / privacy-mode failures
  }
  for (const l of listeners) l();
}

export function usePreviewRole(): Role | null {
  return useSyncExternalStore(subscribe, getPreviewRole, getPreviewRole);
}
